import React, { useEffect, useState } from "react";
import api from "../../utils/api";

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";

import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";

const FeesTab = ({ requestId, requestTypeId, isAdmin }) => {
  const [billingSetting, setBillingSetting] = useState(null);
  const [billingRecord, setBillingRecord] = useState([]);

  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingRecord, setLoadingRecord] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchBillingSettings = async () => {
    try {
      setLoadingSettings(true);
      const res = await api.get("/architectural-billing-settings");
      const all = res.data || [];

      // Find the setting that matches this request's type
      const matched =
        requestTypeId != null
          ? all.find(
              (s) =>
                s.integration_id === requestTypeId ||
                String(s.integration_id) === String(requestTypeId)
            )
          : null;

      setBillingSetting(matched || (all.length === 1 ? all[0] : null));
    } catch (err) {
      console.error("Failed to fetch billing settings:", err);
      setBillingSetting(null);
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchBillingRecord = async () => {
    try {
      setLoadingRecord(true);
      const res = await api.get(`/architectural-requests/${requestId}/billing`);
      const data = res.data;
      // Normalise: API may return a single object or an array
      setBillingRecord(Array.isArray(data) ? data : data ? [data] : []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setBillingRecord([]);
      } else {
        console.error("Failed to fetch billing record:", err);
        setBillingRecord([]);
      }
    } finally {
      setLoadingRecord(false);
    }
  };

  useEffect(() => {
    if (requestId) {
      fetchBillingSettings();
      fetchBillingRecord();
    }
  }, [requestId, requestTypeId]);

  const handleGenerateBilling = async () => {
    try {
      setGenerating(true);
      await api.post(`/architectural-requests/${requestId}/generate-billing`);
      showSnackbar("Billing record generated successfully.");
      fetchBillingRecord();
    } catch (err) {
      console.error("Failed to generate billing record:", err);
      if (err?.response?.status === 409) {
        showSnackbar("A billing record already exists.", "warning");
      } else {
        showSnackbar(
          err?.response?.data?.message || "Failed to generate billing record.",
          "error"
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (val) => {
    if (val == null || val === "") return "N/A";
    return `$${Number(val).toFixed(2)}`;
  };

  const formatDate = (val) => {
    if (!val) return "N/A";
    return new Date(val).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getBillingModeChip = (mode) => {
    const modeMap = {
      flat: { label: "Flat Fee", color: "#2f63ad", bg: "rgba(47,99,173,0.10)" },
      hourly: { label: "Hourly", color: "#0a7c5c", bg: "rgba(10,124,92,0.10)" },
      per_item: { label: "Per Item", color: "#8c5c00", bg: "rgba(140,92,0,0.10)" },
    };
    const m = modeMap[mode] || { label: mode || "Unknown", color: "#5f6b7a", bg: "rgba(95,107,122,0.10)" };
    return (
      <Chip
        label={m.label}
        size="small"
        sx={{
          fontWeight: 700,
          color: m.color,
          backgroundColor: m.bg,
          border: "none",
        }}
      />
    );
  };

  const isLoading = loadingSettings || loadingRecord;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "240px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2.5}>
        {/* Billing Configuration Section */}
        <Grid item xs={12}>
          <Card
            sx={{
              ...pageCardSx,
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderTop: "4px solid #2f63ad",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <MonetizationOnOutlinedIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Fees & Billing
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#5f6b7a", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.72rem" }}
            >
              Billing Configuration
            </Typography>

            {billingSetting ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                      Billing Mode
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {getBillingModeChip(billingSetting.billing_mode)}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                      Rate
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#223142", mt: 0.25 }}>
                      {formatCurrency(billingSetting.rate)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                      Labor Rate
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#223142", mt: 0.25 }}>
                      {formatCurrency(billingSetting.labor_rate)}
                    </Typography>
                  </Box>
                </Grid>

                {billingSetting.integration_id && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                        Integration
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#223142", mt: 0.25 }}>
                        #{billingSetting.integration_id}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Box
                sx={{
                  border: "1px dashed rgba(47,99,173,0.18)",
                  borderRadius: "12px",
                  px: 3,
                  py: 3,
                  textAlign: "center",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  No billing configuration found for this request type.
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Billing Record Section */}
        <Grid item xs={12}>
          <Card sx={{ ...pageCardSx, p: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <ReceiptOutlinedIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Billing Record
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            {billingRecord.length > 0 ? (
              <>
                {billingRecord.map((rec, idx) => (
                  <Card
                    key={idx}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.10)",
                      boxShadow: "none",
                      p: { xs: 1.5, sm: 2 },
                      mb: idx < billingRecord.length - 1 ? 2 : 0,
                      background:
                        "linear-gradient(180deg, rgba(47,99,173,0.03) 0%, rgba(255,255,255,1) 100%)",
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                          Materials Amount
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: "#223142", mt: 0.25 }}>
                          {formatCurrency(rec.materials_amount)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                          Labor Amount
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: "#223142", mt: 0.25 }}>
                          {formatCurrency(rec.labor_amount)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                          Member Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: "#223142", mt: 0.25 }}>
                          {rec.member_number || "N/A"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                          Created At
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#223142", mt: 0.25 }}>
                          {formatDate(rec.created_at)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                          Jonas Export Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={rec.jonas_exported ? "Exported" : "Pending"}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              color: rec.jonas_exported ? "#0a7c5c" : "#8c5c00",
                              backgroundColor: rec.jonas_exported
                                ? "rgba(10,124,92,0.10)"
                                : "rgba(140,92,0,0.10)",
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                ))}

                <Typography
                  variant="body2"
                  sx={{ color: "#6b778c", mt: 1.5, fontStyle: "italic" }}
                >
                  This record is available for review in the Billing Review module.
                </Typography>
              </>
            ) : (
              <Box
                sx={{
                  border: "1px dashed rgba(47,99,173,0.18)",
                  borderRadius: "16px",
                  px: 3,
                  py: 4,
                  textAlign: "center",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#223142", mb: 0.5 }}>
                  No billing record yet.
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
                  A billing record has not been generated for this request.
                </Typography>

                {isAdmin && billingRecord.length === 0 && (
                  <Button
                    variant="contained"
                    startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <MonetizationOnIcon />}
                    onClick={handleGenerateBilling}
                    disabled={generating}
                    sx={{ ...primaryButtonSx, mt: 2 }}
                  >
                    Generate Billing Record
                  </Button>
                )}
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeesTab;
